import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

const ratingEmojis = ["", "😞", "🙁", "😐", "🙂", "😍"];

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => dialogRef.current?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSubmittingRef.current) {
        setRating(0);
        setHoverRating(0);
        setFeedback("");
        setError("");
        onCloseRef.current();
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), textarea:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocusRef.current && document.contains(previousFocusRef.current)) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setFeedback("");
    setError("");
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (rating === 0) {
      setError(t("feedback.rating_error"));
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      await onSubmit({ rating, feedback: feedback.trim() });
      resetForm();
      onClose();
    } catch (submitError) {
      console.error("Error submitting feedback:", submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const activeRating = hoverRating || rating;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-[#111827]/55 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        aria-describedby="feedback-description"
        tabIndex={-1}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-[24px] border border-line bg-paper shadow-2xl sm:rounded-[24px]"
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-4 border-b border-line bg-white px-5 py-4 sm:px-6">
            <div>
              <h2
                id="feedback-title"
                className="font-slab text-[19px] font-bold text-[#1A2233]"
              >
                {t("feedback.title")}
              </h2>
              <p id="feedback-description" className="mt-1 text-[12px] text-[#6B7280]">
                {t("feedback.invite_body")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-[#6B7280] transition-colors hover:bg-paper hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
              aria-label={t("feedback.close")}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="px-5 py-5 sm:px-6">
            <fieldset className="text-center">
              <legend className="w-full text-[14px] font-semibold text-[#1A2233]">
                {t("feedback.question")}
              </legend>

              <div className="mt-3 h-10 text-4xl leading-10" aria-hidden="true">
                {activeRating ? ratingEmojis[activeRating] : "·"}
              </div>

              <div className="mt-2 flex justify-center gap-1" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => {
                      setRating(star);
                      setError("");
                    }}
                    onMouseEnter={() => setHoverRating(star)}
                    disabled={isSubmitting}
                    className={`flex h-11 w-11 items-center justify-center rounded-xl text-[31px] leading-none transition duration-150 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transform-none ${focusRing} ${
                      star <= activeRating ? "text-amber" : "text-[#D5D0C5]"
                    }`}
                    aria-label={t("feedback.star_label", { count: star })}
                    aria-pressed={rating === star}
                  >
                    {star <= activeRating ? "★" : "☆"}
                  </button>
                ))}
              </div>

              <p className="mt-2 min-h-[20px] text-[12px] text-[#6B7280]" aria-live="polite">
                {rating
                  ? t("feedback.rated", { count: rating })
                  : t("feedback.select_rating")}
              </p>
              {error && (
                <p className="mt-1 text-[12px] font-medium text-debit" role="alert">
                  {error}
                </p>
              )}
            </fieldset>

            <div className="mt-5">
              <label
                htmlFor="feedback-comments"
                className="block text-[13px] font-semibold text-[#1A2233]"
              >
                {t("feedback.comments_label")}
              </label>
              <textarea
                id="feedback-comments"
                className={`mt-2 min-h-[112px] w-full resize-y rounded-2xl border border-line bg-white px-4 py-3 text-[14px] text-[#1A2233] placeholder:text-[#9CA3AF] hover:border-primary/30 disabled:cursor-not-allowed disabled:opacity-60 ${focusRing}`}
                placeholder={t("feedback.placeholder")}
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                disabled={isSubmitting}
                maxLength={1000}
              />
              <p className="mt-1 text-right text-[10px] text-[#9CA3AF]" aria-live="polite">
                {feedback.length}/1000
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className={`min-h-[46px] rounded-xl border border-line bg-white px-4 py-2.5 text-[13px] font-semibold text-[#4B5563] transition-colors hover:border-primary/25 hover:bg-primary/[0.03] disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
              >
                {t("feedback.cancel")}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`min-h-[46px] rounded-xl bg-primary px-4 py-2.5 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60 ${focusRing}`}
              >
                {isSubmitting ? t("feedback.submitting") : t("feedback.submit")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
